import { useEffect, useRef, useState } from "react";
import { api, Icon } from "@tc/ui";
import { LEVEL_LABEL } from "./common.js";
import { quickClear, refreshTasks, subscribe, type BarTask, type Feed } from "./feed.js";

export { refreshTasks };

function useFeed(enabled = true): Feed | null {
  const [f, setF] = useState<Feed | null>(null);
  useEffect(() => (enabled ? subscribe(setF) : undefined), [enabled]);
  return f;
}
const pathOf = (l: string | null) => (l ?? "").split(/[?#]/)[0];

/** Menu badges: the shell asks for the counts. */
export function useTaskCounts(enabled: boolean): { total: number; urgent: number } {
  const f = useFeed(enabled);
  return { total: f?.items ?? f?.total ?? 0, urgent: f?.urgent ?? 0 }; // the badge: 37 for one task of 37 documents
}

/** The whole task feed, for the menu badges (each page shows how many tasks point at it). */
export function useTaskFeed(enabled: boolean): Feed | null { return useFeed(enabled); }

const TONE: Record<string, string> = { urgent: "red", action: "amber", info: "blue" };
const detailOf = (t: BarTask, n = 2) => (t.facts?.tiles?.length ? t.facts.tiles.slice(0, n).map((x) => `${x.value} ${x.label}`).join(" · ") : t.body || "");

/**
 * The tasks card at the top of the side menu, in the same style as the home page cards: red when anything is
 * urgent, orange when there's something to do, and just the most pressing one, a tap away.
 */
export function TaskSidebar({ navigate }: { navigate: (p: string) => void }) {
  const f = useFeed();
  if (!f) return <div className="tc-sbt2 loading" aria-busy="true"><div className="tc-sbt2-head"><span className="tc-sbt2-ic" /><b>Tasks</b></div></div>;
  const todo = f.tasks.filter((t) => t.level !== "info").length;
  const tone = f.urgent ? "red" : todo ? "amber" : f.total ? "blue" : "green";
  if (!f.total) return (
    <button className="tc-sbt2 green clear" onClick={() => navigate("/tasks")}>
      <span className="tc-sbt2-head"><span className="tc-sbt2-ic"><Icon name="check" size={17} /></span><b>All clear</b><small>No tasks waiting</small></span>
    </button>
  );
  const top = f.tasks.slice(0, 1);
  return (
    <section className={`tc-sbt2 ${tone}`} aria-label="Your tasks">
      <button className="tc-sbt2-head" onClick={() => navigate("/tasks")}>
        <span className="tc-sbt2-fx" aria-hidden="true"><i /><i /></span>
        <span className="tc-sbt2-ic"><Icon name={f.urgent ? "alert" : "tasks"} size={17} /></span>
        <b>{f.total} task{f.total === 1 ? "" : "s"}</b>
        {f.urgent > 0 && <em>{f.urgent} urgent</em>}
        <Icon name="arrow" size={15} className="tc-sbt2-go" />
      </button>
      <div className="tc-sbt2-list">
        {top.map((t, k) => (
          <button key={t.id} className={`tc-sbt2-row ${TONE[t.level]}`} style={{ ["--k" as string]: k }} onClick={() => navigate(t.link ?? `/tasks?task=${t.id}`)} title={t.body}>
            <span className="tc-sbt2-t"><b>{t.title}</b><small>{t.site && <span>{t.site_name ?? t.site} · </span>}{detailOf(t)}</small></span>
          </button>
        ))}
        {f.total > top.length && <button className="tc-sbt2-more" onClick={() => navigate("/tasks")}>+{f.total - top.length} more</button>}
      </div>
    </section>
  );
}

/**
 * The tasks button by the bell (every screen size): the count at a glance, and a drop-down of what's waiting,
 * urgent first and coloured by how urgent, with Start / Sign off / Got it right there and the task centre a tap away.
 */
export function TaskButton({ navigate }: { navigate: (p: string) => void }) {
  const f = useFeed();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    void refreshTasks();
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close); document.addEventListener("keydown", key);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", key); };
  }, [open]);
  if (!f) return null;
  const go = (p: string) => { setOpen(false); navigate(p); };
  const groups = (["urgent", "action", "info"] as const).map((l) => [l, f.tasks.filter((t) => t.level === l)] as const).filter(([, x]) => x.length);
  return (
    <div className="tc-tbtn" ref={box}>
      <button className={`tc-bell-btn${f.urgent ? " urgent" : ""}`} aria-label={`Tasks: ${f.total}${f.urgent ? `, ${f.urgent} urgent` : ""}`} title={f.total ? `${f.total} task${f.total === 1 ? "" : "s"}${f.urgent ? `, ${f.urgent} urgent` : ""}` : "No tasks"} aria-expanded={open} onClick={() => { setNote(null); setOpen(!open); }}>
        <Icon name="tasks" size={20} />
        {f.total > 0 && <span className={`tc-bell-count${f.urgent ? "" : " calm"}`}>{f.total > 99 ? "99+" : f.total}</span>}
      </button>
      {open && (
        <div className="tc-pop tc-pop-tasks" role="dialog" aria-label="Your tasks">
          <div className="tc-pop-head">
            <div className="tc-pop-title"><b>Tasks</b>{f.total > 0 && <span className={`tc-pop-count${f.urgent ? " red" : ""}`}>{f.total}{f.urgent ? ` · ${f.urgent} urgent` : ""}</span>}</div>
          </div>
          <div className="tc-pop-body">
            {note && <p className="tc-pop-note" role="status"><Icon name="check" size={15} /> {note}</p>}
            {!f.total && <div className="tc-pop-empty"><span><Icon name="check" size={26} /></span><b>All clear</b><small>New tasks show here and on your phone.</small></div>}
            <div className="tc-pn-list">
              {groups.map(([l, list]) => (
                <section key={l}>
                  <h3 className={`tc-pn-day ${TONE[l]}`}>{LEVEL_LABEL[l]} <span>{list.length}</span></h3>
                  {list.slice(0, 8).map((t: BarTask, k) => (
                    <div key={t.id} className={`tc-pn tone-${TONE[t.level]} unread`} style={{ ["--k" as string]: k }}>
                      <button className="tc-pn-main" onClick={() => go(t.link ?? `/tasks?task=${t.id}`)}>
                        <span className="tc-pn-i"><Icon name={(t.icon || "tasks") as Parameters<typeof Icon>[0]["name"]} size={17} /></span>
                        <span className="tc-pn-t"><b>{t.site && <span>{t.site_name ?? t.site} · </span>}{t.title}</b>{detailOf(t, 3) && <small>{detailOf(t, 3)}</small>}</span>
                      </button>
                      {t.check && <button className="tc-pn-act" onClick={() => go(t.link ?? "/tasks/checks")}>Start</button>}
                      {t.clear_mode !== "fix" && !t.check && <button className="tc-pn-act" disabled={busy === t.id} onClick={async () => { setBusy(t.id); try { setNote(await quickClear(t)); } catch (e) { setNote((e as Error).message); } setBusy(null); }}>{t.clear_mode === "sign_off" ? "Sign off" : "Got it"}</button>}
                    </div>
                  ))}
                </section>
              ))}
            </div>
          </div>
          <div className="tc-pop-foot"><button className="tc-btn" onClick={() => go("/tasks")}>Open the task centre <Icon name="arrow" size={15} /></button></div>
        </div>
      )}
    </div>
  );
}

/**
 * The sign-off bar, only on the page a task is about (e.g. "3 days to sign off" on the sign-off page): Open is
 * where you already are, so it offers Sign off / Acknowledge. Everything else lives in the button and the sidebar.
 */
export function PageTaskBar({ path }: { path: string }) {
  const f = useFeed();
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setMsg(null), [path]);
  if (!f?.show || path === "/tasks" || path.startsWith("/tasks/")) return null;
  const t = f.tasks.find((x) => x.link && pathOf(x.link) === path);
  if (!t) return msg ? <div className="tc-taskbar info"><span className="tc-taskbar-note">{msg}</span></div> : null;
  return (
    <section className={`tc-taskbar ${t.level}`} aria-label="Task for this page">
      <span className="tc-taskbar-icon" aria-hidden="true"><Icon name={t.icon || "tasks"} size={18} /></span>
      <div className="tc-taskbar-main">
        <div className="tc-taskbar-top"><span className={`tc-task-level ${t.level}`}>{LEVEL_LABEL[t.level]}</span>{t.site && <span className="tc-task-site">{t.site_name ?? t.site}</span>}<b className="tc-taskbar-title">{t.title}</b></div>
        {msg ? <span className="tc-taskbar-note">{msg}</span> : t.facts?.tiles?.length ? <span className="tc-taskbar-tiles">{t.facts.tiles.slice(0, 3).map((x, k) => <span key={k}><b>{x.value}</b> {x.label}</span>)}</span> : t.body ? <span className="tc-taskbar-note">{t.body}</span> : null}
      </div>
      <div className="tc-taskbar-actions">
        {t.clear_mode !== "fix" && <button className="tc-taskbar-btn primary" disabled={busy} onClick={async () => { setBusy(true); try { setMsg(await quickClear(t)); } catch (e) { setMsg((e as Error).message); } setBusy(false); }}>{t.clear_mode === "sign_off" ? "Sign off" : "Acknowledge"}</button>}
        {t.clear_mode === "fix" && <span className="muted tc-small">Clears itself once it's done</span>}
        <button className="tc-taskbar-btn ghost" title="Mute for an hour" aria-label="Mute for an hour" onClick={async () => { await api(`/api/m/tasks/task/${t.id}/mute`, { body: { minutes: 60 } }); await refreshTasks(); }}><Icon name="mute" size={16} /></button>
      </div>
    </section>
  );
}
